/**
 * TanStack Query hooks for free-weight workouts and records.
 */

import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../client'
import type {
  FreeWeightWorkout,
  FreeWeightRecord,
} from '../types'
import { OPTIONAL_QUERY_DEFAULTS, noneOn404 } from './shared'

/** Today's free-weight workout. Returns null if not logged yet (404); any other failure is an error. */
export function useTodayFreeWorkout() {
  return useQuery({
    queryKey: ['workouts', 'free', 'today'],
    queryFn: () => noneOn404(apiFetch<FreeWeightWorkout>('/api/workouts/free/today')),
    staleTime: 0,
    ...OPTIONAL_QUERY_DEFAULTS,
  })
}

/**
 * Most recent free-weight workout strictly before today (for pre-fill). Returns
 * null if none (404); any other failure is an error.
 *
 * `staleTime: 0`, as for today's: it asks again each time the log opens. A cached 404 must not outlive the day it
 * was read on (a tab kept open past midnight) or a workout logged elsewhere since, because the log reads "there is
 * none" from it to tell an account's first workout (`first_workout_saved`, #708), and a key event sent twice is wrong.
 * `isFetchedAfterMount` is what lets the log tell an answer read during THIS open from the cached one.
 */
export function useLastFreeWorkout() {
  return useQuery({
    queryKey: ['workouts', 'free', 'last'],
    queryFn: () => noneOn404(apiFetch<FreeWeightWorkout>('/api/workouts/free/last')),
    staleTime: 0,
    ...OPTIONAL_QUERY_DEFAULTS,
  })
}

/** All free-weight workouts (newest first, high limit) — used for heatmap date merging. */
export function useFreeWorkoutsList() {
  return useQuery({
    queryKey: ['workouts', 'free', 'list'],
    queryFn: () => apiFetch<FreeWeightWorkout[]>('/api/workouts/free?limit=500'),
    ...OPTIONAL_QUERY_DEFAULTS,
  })
}

/**
 * Records on the free-weight scale only — app, Lanista and barbell loads.
 *
 * `/api/workouts/free/records` serves all three equipment scales, because
 * Technogym machines and Milon stations do hold real records. They must not
 * be pooled with free weights for display: a Milon leg press at 166 is not a
 * barbell 166, and listing them together made "your heaviest movement" a
 * Milon station in Milon units. The Journey page already renders circuit
 * bests in their own block, so pooling also listed them twice.
 */
export function isFreeWeightScale(record: FreeWeightRecord): boolean {
  return record.scale === 'free'
}

/** All-time per-exercise free-weight PRs for the Records tab. */
export function useFreeWeightRecords() {
  return useQuery({
    queryKey: ['free-weights', 'records'],
    queryFn: async () =>
      (await apiFetch<FreeWeightRecord[]>('/api/workouts/free/records')).filter(
        isFreeWeightScale,
      ),
    ...OPTIONAL_QUERY_DEFAULTS,
  })
}
